/**
 * /equipment/pressure-control/drills — crew BOP drills (api#1281, BOP-2).
 * Full width; a row opens the drill on its own page.
 *
 * A DRILL WARNING NEVER BLOCKS. The API computes whether a rig, stack or crew
 * member is past the tenant's drill interval (Settings → Pressure Control,
 * default 7 days, D036 §11.2) and only warns; nothing here refuses anything on
 * it. The rig warnings come from GET /status; a crew's standing comes back
 * with the drill just recorded.
 *
 * CREW TRAINING IS NOT BILLABLE (operator decision): recording a drill makes no
 * ticket line, and the dialog says so.
 *
 * APPEND-ONLY on the API: a drill is never edited. A correction is a new drill.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  PcBadge,
  PcErrorLine,
  PcSectionNav,
  usePcEquipmentOptions,
  usePcPeopleOptions,
} from '../../components/pressure-control/PcSectionNav';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { usePcComponents, usePcDrills, usePcStatus, useRecordPcDrill } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { fromDatetimeLocal, toDatetimeLocal } from '../../lib/datetime-local';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canRecordPcTest,
  PC_DRILL_KIND_LABEL,
  PC_DRILL_STATUS_FILL,
  PC_DRILL_STATUS_LABEL,
  PC_DRILLS,
  pcDay,
  pcDrillKindLabel,
  pcErrorMessage,
  pcSeconds,
} from '../../lib/pressure-control';
import { PC_DRILL_KINDS, type PcDrill, type PcDrillStatus, type PcDrillWrite } from '../../types/pressure-control';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, useOpenCount } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';

/** "Rig 7 · Stack A", or `—`. */
export const pcDrillWhere = (d: Pick<PcDrill, 'rigName' | 'stackName'>) => [d.rigName, d.stackName].filter(Boolean).join(' · ') || '—';
export const pcDrillCrew = (d: PcDrill) => d.crew.map((c) => c.name ?? '—').join(', ') || '—';

/**
 * Record the drill through another door with the same dialog — today a Well
 * Servicing tour sheet's field door (fieldiq-api#1324, recorded PENDING). The
 * vertical's own screen builds it; this file stays vertical-free.
 */
export interface PcDrillRecordTarget {
  title: string;
  /** ISO instant to start "Drilled at" from; now when absent. */
  defaultAt?: string;
  /** Crew ticked when the dialog opens (the sheet's crew). */
  defaultCrew?: string[];
  defaultRef?: string;
  send: (body: PcDrillWrite) => Promise<void>;
  refusal: (e: unknown, fallback: string) => string;
  successMessage: string;
  /** Said under the form, in place of the module's own line. */
  note?: string;
}

export function RecordDrillDialog({
  open, onClose, onRecorded, target,
}: { open: boolean; onClose: () => void; onRecorded?: (s: PcDrillStatus | null) => void; target?: PcDrillRecordTarget }) {
  const tz = useTenantTimeZone();
  const record = useRecordPcDrill();
  const [sending, setSending] = useState(false);
  const openCount = useOpenCount(open);
  const people = usePcPeopleOptions(open);
  const equipment = usePcEquipmentOptions(open);
  const components = usePcComponents(open);
  const stacks = (components.data?.rows ?? []).filter((c) => c.spec.componentClass === 'stack_assembly');
  const [drilledAt, setDrilledAt] = useState('');
  const [kind, setKind] = useState('');
  const [seconds, setSeconds] = useState('');
  const [crew, setCrew] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [rig, setRig] = useState('');
  const [stack, setStack] = useState('');
  const [contextRef, setContextRef] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDrilledAt(toDatetimeLocal(target?.defaultAt ?? new Date().toISOString(), tz));
    setKind(''); setSeconds(''); setCrew(target?.defaultCrew ?? []); setFilter(''); setRig(''); setStack('');
    setContextRef(target?.defaultRef ?? ''); setNotes(''); setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openCount]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (people.data ?? []).filter((p) => !q || p.name.toLowerCase().includes(q) || crew.includes(p.id));
  }, [people.data, filter, crew]);

  const toggle = (id: string) => setCrew((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const submit = async () => {
    setError(null);
    const at = fromDatetimeLocal(drilledAt, tz);
    if (!at) return setError('Enter when the drill was held.');
    if (!kind) return setError('Choose the kind of drill.');
    let secs: number | null = null;
    if (seconds.trim()) {
      secs = Number(seconds.trim());
      if (!Number.isInteger(secs) || secs < 1 || secs > 86400) return setError('Time to secure must be whole seconds (1 to 86,400).');
    }
    if (crew.length === 0) return setError('Tick the crew who took part.');
    if (!rig && !stack) return setError('Choose the rig or the stack the drill was held on.');
    const body: PcDrillWrite = {
      drilled_at: at,
      drill_kind: kind,
      seconds_to_secure: secs,
      crew_user_ids: crew,
      rig_equipment_id: rig || null,
      stack_equipment_id: stack || null,
      context_ref: contextRef.trim() || null,
      notes: notes.trim() || null,
    };
    if (target) {
      setSending(true);
      try {
        await target.send(body);
        toast.success(target.successMessage);
        onClose();
      } catch (e) {
        setError(target.refusal(e, 'The drill could not be recorded.'));
      } finally {
        setSending(false);
      }
      return;
    }
    try {
      const out = await record.mutateAsync(body);
      toast.success('Drill recorded');
      onRecorded?.(out.status);
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, 'The drill could not be recorded.'));
    }
  };

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title={target?.title ?? 'Record a crew BOP drill'}
      wide
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={record.isPending || sending} className="fi-action fi-action--standard fi-action--primary">
            {(record.isPending || sending) && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record drill
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DhField label="Drilled at" hint={tz ? `Your company's clock (${tz}).` : undefined}>
          <input type="datetime-local" className={DH_DATE_INPUT} value={drilledAt} onChange={(e) => setDrilledAt(e.target.value)} aria-label="Drilled at" />
        </DhField>
        <DhField label="Kind">
          <select className={DH_INPUT} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
            <option value="">Choose…</option>
            {PC_DRILL_KINDS.map((k) => <option key={k} value={k}>{PC_DRILL_KIND_LABEL[k]}</option>)}
          </select>
        </DhField>
        <DhField label="Rig" hint="The rig and/or the stack: at least one.">
          <select className={DH_INPUT} value={rig} onChange={(e) => setRig(e.target.value)} aria-label="Rig">
            <option value="">None</option>
            {(equipment.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </DhField>
        <DhField label="Stack">
          <select className={DH_INPUT} value={stack} onChange={(e) => setStack(e.target.value)} aria-label="Stack">
            <option value="">None</option>
            {stacks.map((s) => <option key={s.equipmentId} value={s.equipmentId}>{s.name ?? '—'}</option>)}
          </select>
        </DhField>
        <DhField label="Time to secure (seconds)" hint="Optional.">
          <input className={DH_INPUT} inputMode="numeric" value={seconds} onChange={(e) => setSeconds(e.target.value)} aria-label="Time to secure" />
        </DhField>
        <DhField label="Reference" hint="A job, well or tour number. Optional.">
          <input className={DH_INPUT} value={contextRef} maxLength={100} onChange={(e) => setContextRef(e.target.value)} aria-label="Reference" />
        </DhField>
        <div className="sm:col-span-2">
          <p className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            Crew present · {crew.length} ticked
          </p>
          <input className={DH_INPUT} placeholder="Find a name" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find a crew member" />
          <div className="mt-2 max-h-48 overflow-y-auto rounded-fi-control border border-fieldiq-border" role="group" aria-label="Crew present">
            {people.isLoading ? (
              <p className="px-3 py-2 text-sm text-fieldiq-muted">Loading people…</p>
            ) : people.isError ? (
              <p className="px-3 py-2 text-sm text-fieldiq-text">The people list could not be loaded.</p>
            ) : shown.length === 0 ? (
              <p className="px-3 py-2 text-sm text-fieldiq-text">No one matches.</p>
            ) : (
              shown.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-fieldiq-text hover:bg-fieldiq-dark/50">
                  <input type="checkbox" checked={crew.includes(p.id)} onChange={() => toggle(p.id)} />
                  {p.name}
                </label>
              ))
            )}
          </div>
        </div>
        <div className="sm:col-span-2">
          <DhField label="Notes">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} aria-label="Notes" />
          </DhField>
        </div>
      </div>
      <p className="mt-3 text-xs text-fieldiq-muted">
        {target?.note ?? 'A drill is crew training and is not billed. It cannot be edited once recorded; to correct one, record a new drill.'}
      </p>
      <PcErrorLine message={error} />
    </DhDialog>
  );
}

/** The standings that came back with the drill just recorded: who is still not current. */
function AfterDrill({ status, onDismiss }: { status: PcDrillStatus; onDismiss: () => void }) {
  return (
    <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3" data-testid="pc-drill-after" role="status">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-bold text-white">Drill recorded</p>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="text-fieldiq-muted hover:text-fieldiq-text">
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      {status.warnings.length === 0 ? (
        <p className="mt-1 text-sm text-fieldiq-text">Everyone on this drill, and its rig or stack, is within the {status.warnDays}-day drill interval.</p>
      ) : (
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-fieldiq-text">
          {status.warnings.map((w, i) => <li key={`${w.id}-${i}`}>{w.message}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function PressureControlDrillsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canRecord = canRecordPcTest(user?.role, elevated);
  const tz = useTenantTimeZone();
  const [recording, setRecording] = useState(false);
  const [after, setAfter] = useState<PcDrillStatus | null>(null);
  const query = usePcDrills();
  const statusQuery = usePcStatus();
  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];
  const rigStandings = statusQuery.data?.status?.drills.rigs ?? [];
  const warnDays = statusQuery.data?.status?.drills.warnDays;

  const open = (id: string) => openRecord(`${PC_DRILLS}/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  const when = (d: PcDrill) => formatInstant(d.drilledAt, tz) ?? '—';

  return (
    <div data-testid="pc-drills-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure control"
        title="Crew BOP drills"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canRecord ? (
            <button type="button" onClick={() => setRecording(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Record drill
            </button>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PcSectionNav />
          {after && <AfterDrill status={after} onDismiss={() => setAfter(null)} />}

          <DhPanel
            eyebrow={warnDays ? `Warns after ${warnDays} days · never blocks` : 'Warns only · never blocks'}
            title="Rigs and stacks"
            testId="pc-drill-rigs"
          >
            {statusQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading drill standings" />
              </div>
            ) : statusQuery.isError ? (
              <LoadFailureNotice error={queryFailureError(statusQuery)} kind={queryLoadFailure(statusQuery) ?? 'unreachable'} subject="drill standings" />
            ) : rigStandings.length === 0 ? (
              <DhEmpty>No rig or stack has a drill on record.</DhEmpty>
            ) : (
              <ul className="divide-y divide-fieldiq-border">
                {rigStandings.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid="pc-drill-rig">
                    <span className="font-semibold text-white">{r.name ?? '—'}</span>
                    <span className="flex items-center gap-3 text-fieldiq-text">
                      Last drill {pcDay(r.lastDrilledOn)}
                      {r.daysSince !== null ? ` · ${r.daysSince} ${r.daysSince === 1 ? 'day' : 'days'} ago` : ''}
                      <PcBadge value={r.status} labels={PC_DRILL_STATUS_LABEL} fills={PC_DRILL_STATUS_FILL} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </DhPanel>

          <DhPanel
            eyebrow="Append-only"
            title="Drills"
            testId="pc-drills-register"
            footer={
              !query.isLoading && !failure && rows.length > 0
                ? `${rows.length} shown · newest first · the latest 200${canRecord ? '' : ' · only an admin, office staff or a supervisor can record a drill'}`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading drills" />
              </div>
            ) : failure ? (
              <LoadFailureNotice
                error={queryFailureError(query)}
                kind={failure}
                subject="crew drills"
                allowed="admins, office staff, supervisors, dispatchers, field workers and logistics"
              />
            ) : rows.length === 0 ? (
              <DhEmpty>No crew drill has been recorded.{canRecord && ' Record one with Record drill.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[900px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr>
                        <th scope="col" className="px-4 py-3">Drilled</th>
                        <th scope="col" className="px-4 py-3">Kind</th>
                        <th scope="col" className="px-4 py-3">Time to secure</th>
                        <th scope="col" className="px-4 py-3">Rig / stack</th>
                        <th scope="col" className="px-4 py-3">Crew</th>
                        <th scope="col" className="px-4 py-3">Reference</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((d) => (
                        <tr
                          key={d.id}
                          data-testid="pc-drill-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(d.id)}
                          onKeyDown={rowKeys(d.id)}
                          className="cursor-pointer border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <td className="px-4 py-3 text-sm text-white">{when(d)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDrillKindLabel(d.drillKind)}</td>
                          <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">{pcSeconds(d.secondsToSecure)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDrillWhere(d)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDrillCrew(d)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{d.contextRef ?? '—'}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((d) => (
                    <div
                      key={d.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(d.id)}
                      onKeyDown={rowKeys(d.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="text-sm font-extrabold text-white">{when(d)} · {pcDrillKindLabel(d.drillKind)}</p>
                      <p className="mt-1 text-xs text-fieldiq-muted">{pcDrillWhere(d)} · {pcSeconds(d.secondsToSecure)}</p>
                      <p className="mt-1 text-xs text-fieldiq-text">{pcDrillCrew(d)}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      {canRecord && <RecordDrillDialog open={recording} onClose={() => setRecording(false)} onRecorded={setAfter} />}
    </div>
  );
}
